import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { SERVICE_CENTER_TYPES, toDateTimeInputValue } from "../shared/vehicleFleetConstants";

// One modal for both actions the "Started" stage can trigger: the initial
// serviceStart (mode="start", "Initiated" -> "Started") and the later
// serviceStartUpdate (mode="update", editing the same start details while
// still "Started") - the two only differ in which endpoint they PATCH.
const ServiceStartModal = ({ service, mode = "start", onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const isUpdate = mode === "update";

    const [serviceCenter, setServiceCenter] = useState(service?.serviceCenter || "");
    const [serviceCenterType, setServiceCenterType] = useState(service?.serviceCenterType || SERVICE_CENTER_TYPES[0]);
    const [servicedStartAt, setServicedStartAt] = useState(toDateTimeInputValue(service?.servicedStartAt) || toDateTimeInputValue(new Date()));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async () => {
        if (!serviceCenter.trim() || !servicedStartAt) {
            toast.error("Service center and start date/time are required");
            return;
        }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: `/staff/service/${service?.serviceId}/start${isUpdate ? "/update" : ""}`,
            data: { serviceCenter: serviceCenter.trim(), serviceCenterType, servicedStartAt: new Date(servicedStartAt).toISOString() },
        });
        if (response?.success) {
            toast.success(response?.data?.message || (isUpdate ? "Updated successfully" : "Service started"));
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || `Unable to ${isUpdate ? "update start details" : "start service"}`));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{isUpdate ? "Update Start Details" : "Start Service"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label">Service center</label>
                                <input type="text" className="form-control" value={serviceCenter} onChange={(e) => setServiceCenter(e.target.value)} placeholder="e.g. Downtown Motors" />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Service center type</label>
                                <select className="form-select" value={serviceCenterType} onChange={(e) => setServiceCenterType(e.target.value)}>
                                    {SERVICE_CENTER_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label">Started at</label>
                                <input type="datetime-local" className="form-control" value={servicedStartAt} onChange={(e) => setServicedStartAt(e.target.value)} />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : isUpdate ? "Save" : "Start service"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceStartModal;
